import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { accountsmanagement, HRService } from "../../../config";
import {
  graphqlQuery,
  generateAndExecuteMutation,
  fetchDataEngine,
} from "../../../data/Axios/DynamicService";
import {
  allAccountSubCategories,
  allAccounts,
  allPaymentTypes,
} from "../../../data/Axios/queries";
import { normalizeExpenseTypes } from "../expenseTypeOptions";

const base_url = accountsmanagement.uri;
const hr_base_url = HRService.uri

const ImprestApplicationLineForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData } = props;

  // Read parent Imprest ID from localStorage (same pattern as your invoice)
  const imprestData = JSON.parse(localStorage.getItem("ImprestData") || "{}");
  const parentImprestId = imprestData?.id || null;

  const [initialValues, setInitialValues] = useState({
    id: data?.id || null,
    imprestId: data?.imprestId || parentImprestId || "",
    expenseTypeId: data?.expenseTypeId || "",
    accountId: data?.accountId || "",
    description: data?.description || "",
    amountRequested: data?.amountRequested || 0,
    isActive: data?.isActive ?? 1,
  });

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch Expense Types (Account Subcategories filtered by allowed categories)
  useEffect(() => {
    const fetchExpenseTypes = async () => {
      try {
        const subCategoryData = await graphqlQuery(allAccountSubCategories, base_url);

        const filtered = normalizeExpenseTypes(subCategoryData);

        setExpenseTypes(filtered);
      } catch (error) {
        console.error("Error fetching expense types:", error);
      }
    };

    fetchExpenseTypes();
  }, []);

 
  // Fetch Accounts when Expense Type changes
  const handleExpenseTypeChange = async (event, setFieldValue) => {
    const selectedExpenseTypeId = event.target.value;
    setFieldValue("expenseTypeId", selectedExpenseTypeId);
    setFieldValue("accountId", ""); // Reset account
    setAccounts([]); // Clear previous accounts

    if (!selectedExpenseTypeId) return;

    try {
      const accountData = await fetchDataEngine(
        "allAccountsByColumnValues",
        "accounts",
        { accountSubcategoryId: Number(selectedExpenseTypeId) },
        ["id", "accountNumber", "accountName"],
        base_url
      );

      setAccounts(accountData || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    }
  };

  // Sync parent Imprest ID
  useEffect(() => {
    if (!isEditing && parentImprestId) {
      setInitialValues((prev) => ({
        ...prev,
        imprestId: parentImprestId,
      }));
    }
    if (isEditing && data) {
      setInitialValues((prev) => ({
        ...prev,
        imprestId: data.imprestId || parentImprestId || "",
      }));
    }
  }, [isEditing, data, parentImprestId]);

  // Validation Schema
  const validationSchema = yup.object().shape({
    expenseTypeId: yup.string().required("Expense Type is required"),
    accountId: yup.string().required("Account is required"),
    description: yup.string().required("Description is required"),
    amountRequested: yup
      .number()
      .required("Amount Requested is required")
      .positive("Amount must be greater than zero")
      .typeError("Must be a valid number"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        ...values,
        imprestId: parseInt(parentImprestId || values.imprestId),
        expenseTypeId: parseInt(values.expenseTypeId),
        accountId: parseInt(values.accountId),
    
        amountRequested: parseFloat(values.amountRequested),
        createdBy: userId,
        isActive: values.isActive ? 1 : 0,
      };

      // Clean up
      if (!isEditing && mutationData.id === null) {
        delete mutationData.id;
      }

      const mutationName = isEditing
        ? "updateImprestLine"
        : "createImprestLine";

      const responseKey = isEditing
        ? "imprestLine"
        : "imprestLine";

      const response = await generateAndExecuteMutation(
        mutationName,
        mutationData,
        responseKey,
        hr_base_url
      );

      if (response) {
       
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Imprest line ${isEditing ? "updated" : "created"} successfully.`,
          didOpen: () => {
            const container = Swal.getContainer();
            if (container) container.style.setProperty("z-index", "99999", "important");
          },
        })
         onClose();
         refetchData();
      }
    } catch (error) {
      console.error("Error submitting imprest line:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to save imprest line. Please try again.",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.setProperty("z-index", "99999", "important");
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Imprest Line" : "Create Imprest Line"}
        subtitle={
          isEditing
            ? "Update imprest application line"
            : "Add a new line to imprest application"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
              }}
            >
              {/* Expense Type Dropdown */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Expense Type *</InputLabel>
                <Select
                  name="expenseTypeId"
                  value={values.expenseTypeId}
                  onChange={(e) => handleExpenseTypeChange(e, setFieldValue)}
                  onBlur={handleBlur}
                  error={touched.expenseTypeId && !!errors.expenseTypeId}
                >
                  <MenuItem value="">
                    <em>Select Expense Type</em>
                  </MenuItem>
                  {expenseTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.accountCategoryName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.expenseTypeId && errors.expenseTypeId && (
                  <Typography color="error" variant="caption">
                    {errors.expenseTypeId}
                  </Typography>
                )}
              </FormControl>

              {/* Account Dropdown (Dependent) */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Account *</InputLabel>
                <Select
                  name="accountId"
                  value={values.accountId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountId && !!errors.accountId}
                  disabled={!values.expenseTypeId}
                >
                  <MenuItem value="">
                    <em>Select Account</em>
                  </MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.accountName} ({acc.accountNumber})
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountId && errors.accountId && (
                  <Typography color="error" variant="caption">
                    {errors.accountId}
                  </Typography>
                )}
              </FormControl>


              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Requested Amount */}
              <TextField
                fullWidth
                variant="filled"
                label="Amount Requested *"
                name="amountRequested"
                type="number"
                inputProps={{ step: "0.01" }}
                value={values.amountRequested}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.amountRequested && !!errors.amountRequested}
                helperText={touched.amountRequested && errors.amountRequested}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px" gap="10px">
              <Button onClick={onClose} color="inherit" variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditing
                  ? "Update"
                  : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ImprestApplicationLineForm;
